import { useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
  getProcessDefinitionsLoading,
} from "../features/processDefinitions/processDefinitionsSlice";
import { route } from "../static/route";
import { toastStyles } from "../static/styles";
import { ProcessDefinitionProps } from "../types/processDefinition";

/**
 * Resolves the ?process=<slug> query param (used by every processOrders /
 * processReceives page) into the matching ProcessDefinitionProps. Redirects
 * to the dashboard with a toast if the slug is missing/unknown.
 */
export function useProcessDefinitionFromQuery(notSupportedMessage: string): {
  processDefinition: ProcessDefinitionProps | null;
  loading: boolean;
} {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const definitions = useAppSelector(getProcessDefinitions);
  const loading = useAppSelector(getProcessDefinitionsLoading);
  const slug = searchParams.get("process");

  useEffect(() => {
    if (definitions.length === 0) {
      dispatch(fetchProcessDefinitions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const processDefinition = useMemo(
    () => definitions.find((d) => d.slug === slug) || null,
    [definitions, slug]
  );

  useEffect(() => {
    if (loading || definitions.length === 0) return;
    if (!slug || !processDefinition) {
      toast.error(notSupportedMessage, { ...(toastStyles as any) });
      navigate(route.dashboard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, definitions, slug, processDefinition]);

  return { processDefinition, loading: loading || definitions.length === 0 };
}
